/**
 * Amazon marketplace helpers.
 *
 * Strategy (per user spec):
 *  1. Auto-detect the user's Amazon marketplace from browser locale.
 *  2. If the locale doesn't map to an Amazon marketplace, fall back to
 *     amazon.com (the marketplace most international shoppers default to).
 *  3. Build a search URL - affiliate tag injection happens centrally so
 *     turning on Associates later is a one-line change.
 */

// ISO country code → Amazon TLD. Comprehensive list of live Amazon storefronts
// as of 2025. Anything else falls through to amazon.com.
const COUNTRY_TO_AMAZON_TLD = {
    US: 'com',
    GB: 'co.uk',
    UK: 'co.uk',
    DE: 'de',
    FR: 'fr',
    IT: 'it',
    ES: 'es',
    NL: 'nl',
    SE: 'se',
    PL: 'pl',
    BE: 'com.be',
    CA: 'ca',
    MX: 'com.mx',
    BR: 'com.br',
    JP: 'co.jp',
    AU: 'com.au',
    SG: 'sg',
    AE: 'ae',
    SA: 'sa',
    EG: 'eg',
    TR: 'com.tr',
    IN: 'in',
};

// Some browsers/regions use language-only locales (e.g. "he", "fr").
// Map those to a sensible Amazon marketplace.
const LANG_FALLBACK_TLD = {
    he: 'com',     // Israel - most users buy from amazon.com
    en: 'com',
    de: 'de',
    fr: 'fr',
    it: 'it',
    es: 'es',
    nl: 'nl',
    sv: 'se',
    pl: 'pl',
    pt: 'com.br',
    ja: 'co.jp',
    tr: 'com.tr',
    ar: 'ae',      // Arabic-speaking users → amazon.ae (UAE) most common
};

/**
 * Detect the best Amazon TLD for the current user.
 * Order of preference:
 *   1. Region from navigator.language (e.g. "en-GB" → "co.uk")
 *   2. Language-only fallback (e.g. "he" → "com")
 *   3. amazon.com
 */
export function detectAmazonTld() {
    if (typeof navigator === 'undefined') return 'com';

    const langs = (navigator.languages && navigator.languages.length)
        ? navigator.languages
        : [navigator.language || 'en-US'];

    for (const tag of langs) {
        if (!tag) continue;
        const parts = tag.split('-');
        const lang = parts[0]?.toLowerCase();
        const region = parts[1]?.toUpperCase();
        if (region && COUNTRY_TO_AMAZON_TLD[region]) {
            return COUNTRY_TO_AMAZON_TLD[region];
        }
        if (lang && LANG_FALLBACK_TLD[lang]) {
            return LANG_FALLBACK_TLD[lang];
        }
    }
    return 'com';
}

/**
 * Build an Amazon search URL from a product name (and optional brand).
 *
 * @param {Object} opts
 * @param {string} opts.productName - free-text product name (3-7 word query)
 * @param {string} [opts.brand]     - brand name to include in the search
 * @param {string} [opts.tld]       - override marketplace TLD; defaults to auto-detect
 * @returns {string|null} URL or null if no usable input
 */
export function buildAmazonSearchUrl({ productName, brand, tld } = {}) {
    const name = (productName || '').trim();
    if (!name) return null;
    const query = brand && !name.toLowerCase().includes(brand.toLowerCase())
        ? `${brand} ${name}`
        : name;
    const finalTld = tld || detectAmazonTld();
    const url = new URL(`https://www.amazon.${finalTld}/s`);
    url.searchParams.set('k', query);
    // Affiliate tag - flip on once Associates account is approved.
    // const affiliateTags = { com: 'YOURTAG-20', 'co.uk': 'YOURTAG-21', de: 'YOURTAG-21', /* ... */ };
    // const tag = affiliateTags[finalTld];
    // if (tag) url.searchParams.set('tag', tag);
    return url.toString();
}
